import { inject, Injectable } from "@angular/core";
import {
  PlatformService,
  VisibilityState
} from "src/app/services/platform.service";
import { ProductService } from "src/app/services/product.service";
import { TabService } from "src/app/services/tab.service";
import { ToastService } from "src/app/services/toast.service";
import { routes, routes_admin, routes_rocket } from "../routing/routes";
import { sntTranslate } from "../support/translate";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { tryParseJson } from "src/app/support/json";
import { first } from "lodash-es";

export enum NOTIFICATION_PRIORITY {
  HIGH = "high",
  MEDIUM = "medium",
  LOW = "low"
}

export interface SntNotification {
  id: string;
  type: string;
  message: string;
  sticky?: boolean;
  duration?: number;
  permanent?: boolean;

  priority: NOTIFICATION_PRIORITY;

  read?: boolean;
  sent: string;
  messageEvent?: any;
  actions?: Array<{
    title: () => string;
    do: (providers: { tabService: TabService }, args: any[]) => void;
    variant?: "default" | "primary" | "neutral";
    outline?: boolean;
  }>;
  actionsArgs?: any;
  onDismiss?: () => Promise<void> | void;
}

@Injectable()
export class NotificationService {
  static instance: NotificationService;

  private readonly toastService = inject(ToastService);

  private readonly icons = {
    PMS: "/assets/images/SNOOTS_color.png",
    Rocket: "/assets/favicons/rocket-light.png",
    Snoots: ""
  };

  private readonly platformService = inject(PlatformService);
  private readonly productService = inject(ProductService);
  private readonly tabService = inject(TabService);

  private throttlingList: Array<SntNotification> = [];

  constructor() {
    NotificationService.instance = this;

    this.platformService.pageVisibility$.subscribe(
      (visibilityState: VisibilityState) => {
        if (visibilityState === "visible") {
          this.throttlingList.forEach((notification) => {
            this.publishInternalNotification(notification);
          });

          this.throttlingList = [];
        }
      }
    );
  }

  async publish(notification: SntNotification): Promise<void> {
    switch (true) {
      case this.platformService.isPageVisible(): {
        await this.publishInternalNotification(notification);
        break;
      }
      case this.platformService.isPageHidden(): {
        this.throttlingList.push(notification);
        break;
      }
    }
  }

  clear(): void {
    this.toastService.clear();
  }

  private async publishInternalNotification(
    notification: SntNotification
  ): Promise<void> {
    const id = notification.id;

    switch (notification?.type) {
      case "success": {
        this.toastService.success({
          message: notification.message,
          displayTime: notification.duration,
          onDismiss: notification.onDismiss,
          action:
            notification.actions && notification.actions.length > 0
              ? {
                  label: first(notification.actions || [])!.title(),
                  action: () =>
                    first(notification.actions || [])!.do(
                      {
                        tabService: this.tabService
                      },
                      []
                    )
                }
              : undefined
        });
        break;
      }
      case "error": {
        this.toastService.error({
          message: notification.message,
          displayTime: notification.duration,
          onDismiss: notification.onDismiss,
          action:
            notification.actions && notification.actions.length > 0
              ? {
                  label: first(notification.actions || [])!.title(),
                  action: () =>
                    first(notification.actions || [])!.do(
                      {
                        tabService: this.tabService
                      },
                      []
                    )
                }
              : undefined
        });
        break;
      }
      case "info": {
        const data = tryParseJson<{ resource: string } | null>(
          notification.messageEvent?.data,
          null
        );

        this.toastService.info({
          message: notification.message,
          displayTime: notification.duration,
          onDismiss: notification.onDismiss,
          action: this.getButtonAction(data?.resource)
        });
        break;
      }
      case "warning": {
        this.toastService.warning({
          message: notification.message,
          displayTime: notification.duration,
          sticky: !!notification?.sticky,
          onDismiss: notification.onDismiss
        });
        break;
      }
    }
  }

  private getButtonAction(
    resource: string | undefined
  ): { label: string; action: () => void } | undefined {
    return resource === "export"
      ? {
          label: sntTranslate(marker("Vai alle esportazioni")),
          action: () => {
            const currentProduct = this.productService.getCurrentProduct();

            switch (currentProduct) {
              case "PMS":
                return this.tabService.open(routes.organization.export.list());

              case "Admin":
                return this.tabService.open(routes_admin.exports.list());

              default:
                return this.tabService.open(
                  routes_rocket.configuration.exports.master()
                );
            }
          }
        }
      : undefined;
  }
}
